/**
 * The payer read and payer create for `/stablecoins/invoices/new`.
 *
 * There is no payer hook in `src/hooks/use-invoices.ts` (that module owns the
 * invoice reads and is not this task's to extend), so the create page's own
 * feature folder owns these two calls. They are built only on the functions in
 * `src/features/invoicing/api.ts` — this module never touches `fetch`, and it
 * never invents a payer row.
 *
 * Same three rules as the shared read hooks, for the same reasons:
 *
 *  - **Never throw.** A refusal, a non-JSON body and a dropped connection all
 *    arrive as `error` with the contract's reason code, so the page can render
 *    the failure it has copy for instead of unmounting.
 *  - **Never invent data.** A failed read empties `payers` rather than keeping
 *    the previous list or backfilling one. A payer row that the service did not
 *    return would put a real counterparty's record on screen under the wrong
 *    heading.
 *  - **Out-of-order guard.** A refresh bumps a generation counter so a slower
 *    earlier read cannot land after a newer one.
 *
 * The create is *not* a state mutation the hook replays: the page calls
 * `createPayer` once, per press, with the idempotency key it minted for that
 * press. The hook only folds the service's own response row into the list.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiErrorInfo } from "@/lib/api";
import {
  NETWORK_ERROR_CODE,
  createInvoicePayer,
  listInvoicePayers,
  type ApiResult,
} from "@/features/invoicing/api";
import type { CreatePayerRequest, Payer } from "@/features/invoicing/types";

/** How many payers the picker offers. The contract caps the page size. */
const PAYER_PAGE_LIMIT = 100;

export interface PayersResult {
  payers: Payer[];
  loading: boolean;
  error: ApiErrorInfo | null;
  refresh: () => void;
  /** True while a create is in flight, so the form can disable its submit. */
  creating: boolean;
  /**
   * Create a payer. Returns the service's result unchanged: the caller renders
   * the refusal, and only an `ok` result has a row to select.
   */
  createPayer: (body: CreatePayerRequest, idempotencyKey: string) => Promise<ApiResult<Payer>>;
}

export function usePayers(): PayersResult {
  const [payers, setPayers] = useState<Payer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiErrorInfo | null>(null);
  const [creating, setCreating] = useState(false);
  const generation = useRef(0);

  const load = useCallback(async () => {
    const gen = ++generation.current;
    setLoading(true);
    setError(null);
    try {
      // `archived: false` — an archived payer cannot hold a new invoice, so
      // offering one would be offering a refusal.
      const page = await listInvoicePayers({ archived: false, limit: PAYER_PAGE_LIMIT });
      if (gen !== generation.current) return;
      if (!page.ok) {
        setPayers([]);
        setError(page.error);
      } else {
        setPayers(page.items);
      }
    } catch {
      if (gen !== generation.current) return;
      setPayers([]);
      setError({ reasonCode: NETWORK_ERROR_CODE });
    } finally {
      if (gen === generation.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const createPayer = useCallback(async (body: CreatePayerRequest, idempotencyKey: string): Promise<ApiResult<Payer>> => {
    setCreating(true);
    try {
      const result = await createInvoicePayer(body, { idempotencyKey });
      if (result.ok) {
        // The service's own row, folded in front of the list and de-duplicated
        // by id — a replayed create (same key, same body) returns the same
        // payer and must not appear twice.
        setPayers((previous) => [result.data, ...previous.filter((row) => row.payerId !== result.data.payerId)]);
      }
      return result;
    } catch {
      return { ok: false, error: { reasonCode: NETWORK_ERROR_CODE } };
    } finally {
      setCreating(false);
    }
  }, []);

  return { payers, loading, error, refresh: load, creating, createPayer };
}
